import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { AlertTriangle, Bell, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CriticalAlertsSectionProps {
  data: any;
}

export const CriticalAlertsSection = ({ data }: CriticalAlertsSectionProps) => {
  const navigate = useNavigate();
  const getMetricColor = (
    value: number,
    goodThreshold: number,
    poorThreshold: number,
    reverse = false
  ) => {
    if (reverse) {
      if (value <= goodThreshold) return "text-status-good";
      if (value <= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    } else {
      if (value >= goodThreshold) return "text-status-good";
      if (value >= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    }
  };

  const priorityActions = data?.priorityActions;

  const handleNavigation = () => {
    navigate("/ax/service-order/landing");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-status-poor" />
              Overdue SOs
            </CardTitle>
            <CustomTooltip message="Number of maintenance tasks that have passed their scheduled completion date and require immediate attention. Critical for maintaining operational efficiency." />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-status-poor">
            {priorityActions?.overdueTasks?.value ?? "NA"}
          </div>
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleNavigation}
            >
              View
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bell className="h-4 w-4 text-orange-600" />
              Emergency SOs
            </CardTitle>
            <CustomTooltip message="Number of emergency service orders requiring immediate attention. These are unplanned maintenance activities that impact operations or safety." />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">
            {priorityActions?.emergencyWos?.value ?? "NA"}
          </div>
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleNavigation}
            >
              View
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <XCircle className="h-4 w-4 text-status-poor" />
              SLA Breaches
            </CardTitle>
            <CustomTooltip message="Number of service level agreement violations. Calculated as service orders that exceeded their committed response or resolution time." />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-status-poor">
            {priorityActions?.slaBreaches?.value ?? "NA"}
          </div>
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleNavigation}
            >
              View
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" />
              SO Completion Rate
            </CardTitle>
            <CustomTooltip message="Percentage of service orders completed on time. Calculated as (completed service orders / total work orders) × 100 for the reporting period." />
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${getMetricColor(
              priorityActions?.woCompletionRate?.value,
              90,
              80
            )}`}
          >
            {priorityActions?.woCompletionRate?.value ?? "NA"}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className={`h-2 rounded-full ${
                priorityActions?.woCompletionRate?.value >= 80
                  ? "bg-green-500"
                  : priorityActions?.woCompletionRate?.value >= 70
                  ? "bg-yellow-500"
                  : "bg-red-500"
              }`}
              style={{
                width: `${priorityActions?.woCompletionRate?.value}%`,
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
